import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, getToken } from '../App';
import AuthModal from './AuthModal';
import { calculateAll, fmtNum } from '../utils/numerology';

const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];
const YEARS = [];
for (let y = 2026; y >= 1926; y--) YEARS.push(y);

function AddMemberModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !day || !month || !year) return;
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/family/members`,
        { name: name.trim(), day: parseInt(day), month: parseInt(month), year: parseInt(year), pronouns: pronouns || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAdded(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose} data-testid="add-member-overlay">
      <div className="auth-modal-box" onClick={e => e.stopPropagation()} data-testid="add-member-modal">
        <button className="auth-close" onClick={onClose}>×</button>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, marginBottom: 4 }}>Add family member</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 24 }}>Add a child, partner, or yourself to your family</div>
        {error && <div className="auth-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <label className="field-label">Full name</label>
          <input className="field-input" value={name} onChange={e => setName(e.target.value)} placeholder="Full name at birth" required data-testid="member-name-input" />
          <label className="field-label">Date of birth</label>
          <div className="dob-wrap">
            <select value={day} onChange={e => setDay(e.target.value)} required data-testid="member-day">
              <option value="">Day</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={month} onChange={e => setMonth(e.target.value)} required data-testid="member-month">
              <option value="">Month</option>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <select value={year} onChange={e => setYear(e.target.value)} required data-testid="member-year">
              <option value="">Year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <label className="field-label" style={{ marginTop: 14 }}>
            Pronouns <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 11 }}>(optional)</span>
          </label>
          <div data-testid="member-pronoun-buttons" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              { v: 'he/him', l: 'He / Him' },
              { v: 'she/her', l: 'She / Her' },
              { v: 'they/them', l: 'They / Them' },
            ].map(p => (
              <button
                key={p.v}
                type="button"
                className={`stage-btn ${pronouns === p.v ? 'active' : ''}`}
                onClick={() => setPronouns(prev => prev === p.v ? '' : p.v)}
                data-testid={`member-pronoun-${p.v.replace('/', '-')}`}
                style={{ flex: '1 1 30%', minWidth: 90, padding: '10px 12px' }}
              >
                <span>{p.l}</span>
              </button>
            ))}
          </div>
          <button type="submit" className="auth-submit" disabled={loading || !name.trim() || !day || !month || !year} style={{ marginTop: 20 }} data-testid="add-member-submit">
            {loading ? 'Adding...' : 'Add to family'}
          </button>
        </form>
      </div>
    </div>
  );
}

function MemberCard({ member, onDelete }) {
  const navigate = useNavigate();
  const nums = member.numbers || {};
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleNewReading = () => {
    const pronounsParam = member.pronouns ? `&pronouns=${encodeURIComponent(member.pronouns)}` : '';
    navigate(`/tool?name=${encodeURIComponent(member.name)}&day=${member.day}&month=${member.month}&year=${member.year}&mode=child${pronounsParam}`);
  };

  return (
    <div className="member-card" data-testid={`member-card-${member._id}`}>
      {/* Number circle */}
      <div className="member-circle">
        <div className="member-lp">{fmtNum(nums.life_path)}</div>
        <div className="member-lp-label">Life Path</div>
      </div>

      <div className="member-name">{member.name}</div>

      {nums.star_sign && (
        <div className="member-sign">{nums.star_sign}</div>
      )}

      <div className="member-chips">
        <span className="member-chip">Expression {fmtNum(nums.expression)}</span>
        <span className="member-chip">Soul Urge {fmtNum(nums.soul_urge)}</span>
        <span className="member-chip">Maturity {fmtNum(nums.maturity)}</span>
      </div>

      <div className="member-actions">
        <button className="member-read-btn" onClick={handleNewReading} data-testid={`new-reading-${member._id}`}>
          New reading
        </button>
        <button className="member-view-btn" onClick={() => navigate('/saved')} data-testid={`view-readings-${member._id}`}>
          View readings
        </button>
      </div>

      {!confirmDelete ? (
        <button className="member-delete-btn" onClick={() => setConfirmDelete(true)} data-testid={`delete-member-${member._id}`}>Remove</button>
      ) : (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }}>
          <button onClick={() => onDelete(member._id)} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 100, padding: '5px 14px', fontSize: 11, cursor: 'pointer' }}>Confirm remove</button>
          <button onClick={() => setConfirmDelete(false)} style={{ background: 'none', border: '1px solid var(--border-strong)', borderRadius: 100, padding: '5px 14px', fontSize: 11, cursor: 'pointer', color: 'var(--muted)' }}>Cancel</button>
        </div>
      )}
    </div>
  );
}

export default function FamilyDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const load = async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/family/members`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); else setLoading(false); }, [user]);

  const handleDelete = async (memberId) => {
    const token = getToken();
    try {
      await axios.delete(`${API}/family/members/${memberId}`, { headers: { Authorization: `Bearer ${token}` } });
      setMembers(prev => prev.filter(m => m._id !== memberId));
    } catch { /* silent */ }
  };

  if (!user) {
    return (
      <div style={{ maxWidth: 500, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300, marginBottom: 8 }}>Your family</div>
        <p style={{ color: 'var(--muted)', marginBottom: 28, fontSize: 15 }}>Create an account to build your family and save readings for everyone.</p>
        <button className="tool-btn" onClick={() => setShowAuth(true)} data-testid="family-signin-btn">Create an account</button>
        {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => { setShowAuth(false); load(); }} initialTab="register" />}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 32px' }} data-testid="family-dashboard">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 300, marginBottom: 4 }}>Your family</div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>{user.name}'s family profile</div>
        </div>
        <button className="tool-btn" onClick={() => setShowAddModal(true)} data-testid="add-member-btn">
          + Add member
        </button>
      </div>

      {/* Coming soon teaser banner */}
      <div className="family-teaser-banner" data-testid="family-teaser">
        <div className="family-teaser-icon">✦</div>
        <div className="family-teaser-text">
          <strong>Full family profiles are launching soon</strong>
          <span>Compare numbers across your family, track readings over time, and get deeper insights into your family's blueprint together.</span>
        </div>
        <button
          onClick={() => navigate('/#about-section')}
          style={{ background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 100, padding: '8px 18px', fontSize: 12, color: 'white', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: "'Figtree', sans-serif" }}
          data-testid="join-waitlist-family-btn"
        >
          Join waitlist
        </button>
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="loading-dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
        </div>
      )}

      {!loading && (
        <div className="family-grid" data-testid="family-grid">
          {members.map(m => (
            <MemberCard key={m._id} member={m} onDelete={handleDelete} />
          ))}

          {/* Add member card */}
          <div className="member-add-card" onClick={() => setShowAddModal(true)} data-testid="add-member-card">
            <div style={{ fontSize: 32, color: 'var(--muted)', marginBottom: 10 }}>+</div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 300, color: 'var(--muted)' }}>Add member</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, opacity: 0.7 }}>Child, partner, or yourself</div>
          </div>
        </div>
      )}

      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onAdded={member => setMembers(prev => [...prev, member])}
        />
      )}
    </div>
  );
}
